import { Box } from '@mui/material';
import { PORTADA_C_POSICIONES_GRANDES, type PublicPortadaSlot } from '@ahora-patagonia/contracts';
import { NotaFrenteC, NotaSecundaria } from './PortadaCeldas.js';
import type { PartePortada } from './portadaPartes.js';

export interface PortadaCProps {
  slots: PublicPortadaSlot[];
  parte: PartePortada;
}

/**
 * Maqueta C — dos pares + 3: dos notas del **mismo peso** se disputan el frente en mitades iguales,
 * y debajo van las tres chicas que también cierran A.
 *
 * En escritorio el frente corre a ancho completo por encima de la columna lateral y las tres chicas
 * quedan en la columna principal, así que Última hora arranca **debajo de las dos grandes**. Esa
 * partición es la que resuelve `parte` (ver `HomeView`); por debajo de 1200 no hay lateral, así que
 * el bloque se arma entero en el flujo como cualquier otro.
 */
export function PortadaC({ slots, parte }: PortadaCProps) {
  const grandes = slots.filter((s) => s.position <= PORTADA_C_POSICIONES_GRANDES);
  const chicas = slots.filter((s) => s.position > PORTADA_C_POSICIONES_GRANDES);

  const frente = (
    <Box
      sx={{
        display: 'grid',
        // Dos columnas **iguales**: es lo que hace que las dos notas pesen lo mismo. Apilan recién
        // en teléfono, donde no hay ancho para dos titulares de 26 px lado a lado.
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
        gap: 2.5,
      }}
    >
      {grandes.map((slot) => (
        <Box key={slot.position}>
          {slot.nota && <NotaFrenteC nota={slot.nota} prioritaria={slot.position === 1} />}
        </Box>
      ))}
    </Box>
  );

  const resto = (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
        gap: 2.5,
      }}
    >
      {chicas.map((slot) => (
        <Box key={slot.position}>
          {/* La regla fina separa la fila de tres del frente: sin ella las chicas se leen como
              continuación de las grandes en vez de como el escalón siguiente. */}
          {slot.nota && <NotaSecundaria nota={slot.nota} regla />}
        </Box>
      ))}
    </Box>
  );

  if (parte === 'frente') return frente;
  if (parte === 'resto') return resto;

  return (
    <>
      {frente}
      <Box sx={{ mt: 2.5 }}>{resto}</Box>
    </>
  );
}
